import { deriveHomeSettingsProvenance } from "../../generate/deriveSettingsProvenance";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_138: DocMigration = {
  maxSchemaVersion: "0.0.138",
  description:
    "Update PorchSettings, RoofSettings & AwningSettings to rely on presence of bracketPropId to determine prop rather than additional boolean or discriminator field",
  apply(json: any) {
    const migratedSettings = {};
    const migratedRoofs = {};
    const porchSettings = {};
    const migratedRoomLayouts = {};

    const settingsProvenance: any = deriveHomeSettingsProvenance({
      settings: json.settings ?? {},
      settingsSets: json.settingsSets ?? {},
      optionChoices: json.design ?? {},
      optionSets: json.params ?? {},
    });

    // handle settings migration
    for (const id in json.settings) {
      const setting = json.settings[id];

      /*
        Remove porchColumnBracketType property from porch column settings in favor of setting an optional columnBracketPropId
       */

      if (isPorchSetting(setting)) {
        // store porch specific settings for later use when migrating porch room settings
        porchSettings[setting.id] = setting;

        const targetPorchKey = isRearPorchSetting(setting) ? "rearPorch" : "porch";
        const columnBracketTypeSourceId = settingsProvenance?.[targetPorchKey]?.columnBracketType;
        const porchSettingProvenance = json.settings[columnBracketTypeSourceId] ?? setting;

        switch (porchSettingProvenance.columnBracketType) {
          case "FRENCH_COUNTRY": {
            migratedSettings[id] = {
              ...setting,
              columnBracketPropId: "bracketFrenchCountry",
            };
            break;
          }
          case "TALL_TAPERED": {
            migratedSettings[id] = {
              ...setting,
              columnBracketPropId: "bracketTallTapered",
            };
            break;
          }
          case "TRIANGLE": {
            migratedSettings[id] = {
              ...setting,
              columnBracketPropId: "TRIANGLE",
            };
            break;
          }
          case "DISABLED":
            migratedSettings[id] = {
              ...setting,
              columnBracketPropId: undefined,
            };
            break;
          default:
            // if rear porch set key so as not to be overwritten when settings sets are merged
            if (isRearPorchSetting(setting)) {
              migratedSettings[id] = {
                ...setting,
                columnBracketPropId: undefined,
              };
            } else {
              migratedSettings[id] = setting;
            }
            break;
        }
        delete migratedSettings[id].columnBracketType;

        continue;
      }

      /*
        Remove includeRakeBrackets property from roof settings in favor of relying on optional rakeBracketPropId; 
        set default bracket prop if previously enabled
       */

      if (isRoofSetting(setting)) {
        const includeRakeBracketsSourceId = settingsProvenance?.roof?.includeRakeBrackets;
        const roofSettingProvenance = json.settings[includeRakeBracketsSourceId] ?? setting;

        if (roofSettingProvenance.includeRakeBrackets !== undefined) {
          migratedSettings[id] = {
            ...setting,
            rakeBracketPropId: roofSettingProvenance.includeRakeBrackets ? "rakeBracket" : "",
          };
        } else {
          migratedSettings[id] = setting;
        }
        delete migratedSettings[id].includeRakeBrackets;

        continue;
      }

      /*
        Openings defined on Opening patterns defined within room layouts each have an awningSettings object
        that must be migrated
       */

      for (const id in json.roomLayouts) {
        const roomLayout = json.roomLayouts[id];

        const { openingPatterns } = roomLayout;

        if (!openingPatterns) {
          migratedRoomLayouts[id] = roomLayout;
          continue;
        }

        const migratedOpeningPatterns: any[] = [];

        for (const openingPattern of openingPatterns) {
          if (!openingPattern?.openings) {
            migratedOpeningPatterns.push(openingPattern);
            continue;
          }

          const migratedOpenings: any[] = [];

          for (const opening of openingPattern.openings) {
            if (!opening.awningSettings || opening.awningSettings.awningType === "DISABLED") {
              migratedOpenings.push(opening);
              continue;
            }

            let migratedOpening;

            const { bracketType } = opening.awningSettings;

            if (bracketType !== undefined) {
              migratedOpening = {
                ...opening,
                awningSettings: {
                  ...opening.awningSettings,
                  awningBracketPropId: bracketType === "PROP" ? "awningBracket" : undefined,
                },
              };
            } else {
              migratedOpening = opening;
            }

            delete migratedOpening.awningSettings.bracketType;
            migratedOpenings.push(migratedOpening);
          }

          migratedOpeningPatterns.push({
            ...openingPattern,
            openings: migratedOpenings,
          });
        }

        migratedRoomLayouts[id] = {
          ...roomLayout,
          openingPatterns: migratedOpeningPatterns,
        };
      }

      /*
        Remove awningBracketType property from awning settings in favor of relying on optional awningBracketPropId;
        set default bracket prop if previous type was "PROP" and not "DISABLED"
       */

      if (isAwningSetting(setting)) {
        const bracketTypeSourceId = settingsProvenance?.awning?.bracketType;
        const awningSettingProvenance = json.settings[bracketTypeSourceId] ?? setting;

        migratedSettings[id] = {
          ...setting,
          awningBracketPropId: awningSettingProvenance.bracketType !== "DISABLED" ? "awningBracket" : undefined,
        };
        delete migratedSettings[id].bracketType;

        continue;
      }

      migratedSettings[id] = setting;
    }

    /*
      Roofs have their own settings object, so they must also be migrated
     */

    for (const id in json.roofs) {
      const roof = json.roofs[id];

      if (!roof.roofSettings || !Object.keys(roof.roofSettings).length) {
        migratedRoofs[id] = roof;
        continue;
      }

      let updatedRoofSettings;
      if (roof.roofSettings.includeRakeBrackets !== undefined) {
        updatedRoofSettings = {
          ...roof.roofSettings,
          rakeBracketPropId: roof.roofSettings.includeRakeBrackets ? "rakeBracket" : "",
        };
      } else {
        updatedRoofSettings = { ...roof.roofSettings };
      }
      delete updatedRoofSettings.includeRakeBrackets;

      migratedRoofs[id] = {
        ...roof,
        roofSettings: updatedRoofSettings,
      };
    }

    /*
      Rooms have their own settings object, but at this stage they have not been assigned directly on
      the room object, so we must look up the room's settings on the top-level doc through tying the
      room to the setting's parent id
     */

    for (const id in json.rooms) {
      const room = json.rooms[id];

      const porchRoomSetting: any = Object.values(porchSettings).find((x) => x && x["parentId"] === room.id);

      if (!["FRONT_PORCH", "REAR_PORCH"].includes(room.type) || !porchRoomSetting) {
        continue;
      }

      migratedSettings[porchRoomSetting.id] = migratePorchSettings(porchRoomSetting);
    }

    json = {
      ...json,
      settings: migratedSettings,
      roofs: migratedRoofs,
      roomLayouts: migratedRoomLayouts,
    };

    return json;
  },
};

export default migration_0_0_138;

// Utils for increasing readability

function isPorchSetting(setting: any): boolean {
  return ["PORCH_SETTINGS", "REAR_PORCH_SETTINGS"].includes(setting?.type);
}

function isRearPorchSetting(setting: any): boolean {
  return setting?.type === "REAR_PORCH_SETTINGS";
}

function isRoofSetting(setting: any): boolean {
  return setting?.type === "ROOF_SETTINGS";
}

function isAwningSetting(setting: any): boolean {
  return setting?.type === "AWNING_SETTINGS";
}

function migratePorchSettings(porchSettings): any {
  let migratedPorchSettings: any;

  switch (porchSettings.columnBracketType) {
    case "DISABLED":
      migratedPorchSettings = {
        ...porchSettings,
        columnBracketPropId: "",
      };
      break;
    case "FRENCH_COUNTRY": {
      migratedPorchSettings = {
        ...porchSettings,
        columnBracketPropId: "bracketFrenchCountry",
      };
      break;
    }
    case "TALL_TAPERED": {
      migratedPorchSettings = {
        ...porchSettings,
        columnBracketPropId: "bracketTallTapered",
      };
      break;
    }
    case "TRIANGLE": {
      migratedPorchSettings = {
        ...porchSettings,
        columnBracketPropId: "TRIANGLE",
      };
      break;
    }
    default:
      migratedPorchSettings = porchSettings;
      break;
  }

  return migratedPorchSettings;
}
